import { Backdrop, Box } from '@mui/material';
import UPButton from 'components/button';
import { UPDropdownItem } from 'components/dropdown';
import UPDropdownWithSearch from 'components/dropdown-with-search';
import ErrorBar from 'components/error-bar';
import UPInput from 'components/input';
import WarningBar from 'components/warning-bar';
import { CompanyRole } from 'constants/CompanyRole';
import { useContractForm, useContractModalDropdownData } from 'hooks/contracts';
import useSelectableFunctions, { withStoredFunction } from 'hooks/contracts/useSelectableFunctions';
import { generateEmployeesDropdownItems } from 'modules/contracts/helpers';
import { ContractDateContainer } from 'modules/contracts/styles';
import { IContractDate } from 'modules/contracts/types';
import dayjs from 'utils/dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, ControllerRenderProps, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getDimonaNumberAction,
  getWageAction,
  validateAndSaveEditContractAction,
} from 'store/actions/contract-actions';
import { getDimonaNumberErrorSelector, getDimonaNumberSuccessSelector } from 'store/selectors/contracts-selector';
import { profileSelector } from 'store/selectors/profile-selectors';
import theme from 'theme';
import {
  CONTRACTS__DIMONA,
  CONTRACTS__EDIT_CONTRACT,
  CONTRACTS__VIEW_CONTRACT,
  GENERAL__CANCEL_BUTTON,
  GENERAL__NAME,
  GENERAL__SAVE_BUTTON,
  GENERAL__SELECT,
  GENERAL__WORKERCLASS,
} from 'translations/constants';
import { IWageData } from 'types/contract';
import { ContractFormFields, IContractFormFields } from 'types/contract/IContractFormFields';
import { getInputErrorText, getWorkerclassLabelFromCode } from 'utils/helpers';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/Close.svg';
import { addTimespans } from '../add/helpers';
import {
  AddContractModalContainer,
  ModalCloseIconStyled,
  ModalContentContainer,
  ModalFooter,
  ModalHeaderContract,
  ModalTitle,
  ModalTitleContainer,
  ModalWrapperContract,
} from '../add/styles';
import ContractModalsBottomSection from '../ContractModalsBottomSection';
import { areAllTimeSpansValidEdit, onError } from '../helpers';
import { EditContractModalProps } from '../types';
import EditDayPickerRow from './EditDayPickerRow';

const EditContractModal: React.FC<EditContractModalProps> = props => {
  const {
    onClose,
    onSuccess,
    companyId,
    week,
    weekDate,
    contractToEdit,
    isContractSlot,
    defaultBreakTime,
    isViewOnly,
  } = props;

  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const profile = useAppSelector(profileSelector);
  const dimonaError = useAppSelector(getDimonaNumberErrorSelector);
  const dimonaSuccess = useAppSelector(getDimonaNumberSuccessSelector);
  const isEmployee = profile.role === CompanyRole.EMPLOYEE;
  const isAdmin = profile.role === CompanyRole.ADMIN;
  const isWageVisible = profile.showWages;

  const { functions, costCenters, companyEmployees, locationsDropdownItems } = useContractModalDropdownData(
    +companyId,
    isEmployee,
    week,
    weekDate,
  );

  const contractEmployeeIds = useMemo(
    () => (isContractSlot ? [] : [contractToEdit.employee.id]),
    [isContractSlot, contractToEdit],
  );
  const { functionsFor } = useSelectableFunctions(+companyId, contractEmployeeIds, functions);

  const selectableFunctions = useMemo(
    () => withStoredFunction(functionsFor(contractEmployeeIds), functions, contractToEdit.functionId),
    [functionsFor, contractEmployeeIds, functions, contractToEdit.functionId],
  );

  const [dateRows, setDateRows] = useState<IContractDate[]>(contractToEdit.dates);

  const [currentStartWorkError, setCurrentStartWorkError] = useState(false);
  const [currentEndWorkError, setCurrentEndWorkError] = useState(false);
  const [hourFormatErrorIndex, setHourFormatErrorIndex] = useState(-1);
  const [dimonaId, setDimonaId] = useState('');

  const permanent = !isContractSlot
    ? companyEmployees.find(employee => employee.id === contractToEdit.employee.id)?.permanent
    : false;

  const employees: UPDropdownItem[] = !isContractSlot
    ? [
        {
          value: contractToEdit.employee.id.toString(),
          label: `${contractToEdit.employee.firstName} ${contractToEdit.employee.lastName}`,
        },
      ]
    : generateEmployeesDropdownItems(companyEmployees);

  const { handleSubmit, control, setValue, watch, formState, getValues } = useContractForm(
    functions,
    costCenters,
    contractToEdit,
  );
  const { errors } = formState;

  const functionWatch = watch(ContractFormFields.FUNCTION_ID);
  const locationWatch = watch(ContractFormFields.LOCATION_ID);
  const costCenterWatch = watch(ContractFormFields.COST_CENTER_ID);
  const employeesWatch = watch(ContractFormFields.EMPLOYEE_IDS);

  const isSaveDisabled = () => {
    return currentStartWorkError || currentEndWorkError;
  };

  const onChangeStartBreak = (value: string, rowIndex: number) => {
    let newBreak = null;
    if (defaultBreakTime && value) {
      newBreak = addTimespans(value, defaultBreakTime);
    }
    const newDateRows: IContractDate[] = dateRows.map((dateRow, index) => {
      if (rowIndex === index) {
        return {
          ...dateRow,
          startBreak: value,
          endBreak: newBreak ?? dateRow.endBreak,
        };
      }
      return dateRow;
    });

    setDateRows(newDateRows);
  };

  const onChangeHours = useCallback(
    (value: string, type: string, rowIndex: number) => {
      const newDateRows: IContractDate[] = dateRows.map((dateRow, index) => {
        if (index === rowIndex) {
          return {
            ...dateRow,
            [type]: value,
          };
        }
        return dateRow;
      });

      setDateRows(newDateRows);
    },
    [dateRows],
  );

  const validateAndSaveContract: SubmitHandler<IContractFormFields> = (contractForm: IContractFormFields) => {
    dispatch(
      validateAndSaveEditContractAction({
        contract: {
          contractId: contractForm.id,
          employeeId: contractForm.employeeIds[0],
          companyId: contractToEdit.companyId,
          contractType: contractForm.workerclass,
          functionId: +contractForm.functionId,
          costCenterId: contractForm.costCenterId,
          locationId: +contractForm.locationId,
          workingLocation: contractForm.workingLocation,
          wage: +contractForm.wage,
          dates: dateRows,
        },
        companyId: contractToEdit.companyId,
      }),
    ).then(result => {
      if (result.payload) {
        onSuccess();
      }
    });
  };

  const getContractStartDate = useCallback((): string => {
    if (dateRows?.length > 0) {
      const dt = dateRows
        .sort((a, b) => {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        })
        .reverse()[0].date;
      return dayjs(dt).format();
    }

    return dayjs().format();
  }, [dateRows]);

  useEffect(() => {
    dispatch(
      getDimonaNumberAction({
        companyId: +companyId,
        contractId: contractToEdit.contractId,
      }),
    );
  }, [companyId, contractToEdit, dispatch]);

  useEffect(() => {
    if (contractToEdit.dimonaId) {
      setDimonaId(contractToEdit.dimonaId.toString());
      return;
    }
    if (contractToEdit.warningMessage && contractToEdit.warningMessage !== '') {
      return;
    }
    if (contractToEdit.dimonaId === '' && !dimonaError) {
      setDimonaId('');
      return;
    }
    if (dimonaSuccess && contractToEdit.contractId === dimonaSuccess.contractId) {
      setDimonaId(dimonaSuccess.dimonaId.toString());
      return;
    }
    if (dimonaError && contractToEdit.contractId === dimonaError.contractId) {
      setDimonaId('');
    }
  }, [contractToEdit, dimonaError, dimonaSuccess]);

  useEffect(() => {
    if (
      isEmployee &&
      !permanent &&
      isWageVisible &&
      employeesWatch[0] &&
      functionWatch === contractToEdit.functionId.toString()
    ) {
      const wageData: IWageData = {
        employeeId: employeesWatch[0],
        functionId: +functionWatch,
        companyId: +companyId,
        contractStartDate: getContractStartDate(),
      };

      dispatch(getWageAction(wageData)).then(response => {
        const wage = response.payload.toString();
        setValue(`${ContractFormFields.WAGE}`, wage);
      });
    }
  }, [
    isEmployee,
    companyId,
    employeesWatch,
    functionWatch,
    permanent,
    isWageVisible,
    dispatch,
    setValue,
    contractToEdit.functionId,
    getContractStartDate,
  ]);

  const onSubmit = () => {
    let isSavePossible = true;

    const dateRowWithErrors = dateRows.find(row => !row.startHour || !row.endHour);

    if (dateRowWithErrors) {
      setCurrentStartWorkError(!dateRowWithErrors.startHour);
      setCurrentEndWorkError(!dateRowWithErrors.endHour);
      isSavePossible = false;
    } else {
      isSavePossible = areAllTimeSpansValidEdit(dateRows, setHourFormatErrorIndex);
    }

    if (isSavePossible) {
      handleSubmit(validateAndSaveContract, onError)();
    }
  };

  const onEmployeeChange =
    (field: ControllerRenderProps<IContractFormFields, ContractFormFields.EMPLOYEE_IDS>) => (newValue: string) => {
      const currentEmployee = companyEmployees.find(employee => employee.id === +newValue);
      field.onChange([+newValue]);
      setValue(`${ContractFormFields.WORKERCLASS}`, currentEmployee?.workerclass);
    };

  return (
    <Backdrop open sx={{ zIndex: 1 }}>
      <ModalWrapperContract open>
        <AddContractModalContainer>
          <ModalHeaderContract>
            <ModalTitleContainer>
              <Box display="flex" flexDirection="column">
                <ModalTitle>{isViewOnly ? t(CONTRACTS__VIEW_CONTRACT) : t(CONTRACTS__EDIT_CONTRACT)}</ModalTitle>
              </Box>
              <ModalCloseIconStyled onClick={onClose} id="contract-edit-close-modal-icon">
                <CloseIcon fill={theme.palette.secondary.contrastText} />
              </ModalCloseIconStyled>
            </ModalTitleContainer>
            {contractToEdit.errorMessage && (
              <Box flex={1} mt={1}>
                <ErrorBar message={t(contractToEdit.errorMessage)} />
              </Box>
            )}
            {contractToEdit.warningMessage && (
              <Box flex={1} mt={1}>
                <WarningBar message={t(contractToEdit.warningMessage)} />
              </Box>
            )}

            <Box flex={1} display="flex" gap={theme.spacing(2)} marginTop={theme.spacing(2.5)}>
              <Box flex={1}>
                <Controller
                  name={ContractFormFields.EMPLOYEE_IDS}
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <UPDropdownWithSearch
                      value={field.value}
                      onChange={onEmployeeChange(field)}
                      placeholder={t(GENERAL__SELECT)}
                      items={employees}
                      label={t(GENERAL__NAME)}
                      required
                      helperText={getInputErrorText(errors, ContractFormFields.EMPLOYEE_IDS)}
                      error={!!errors.employeeIds}
                      disabled={!isContractSlot || isEmployee || isViewOnly}
                      id="contract-edit-employee-dropdown"
                    />
                  )}
                />
              </Box>
              <Box flex={1}>
                <Controller
                  name={ContractFormFields.WORKERCLASS}
                  control={control}
                  render={({ field }) => (
                    <UPInput
                      value={getWorkerclassLabelFromCode(field.value)}
                      onChange={null}
                      label={t(GENERAL__WORKERCLASS)}
                      placeholder=""
                      disabled
                      id="contract-edit-workerclass-input"
                    />
                  )}
                />
              </Box>
              {!contractToEdit?.errorMessage && (
                <Box flex={1}>
                  <UPInput
                    value={dimonaId}
                    onChange={null}
                    label={t(CONTRACTS__DIMONA)}
                    placeholder=""
                    disabled
                    id="contract-edit-dimona-input"
                  />
                </Box>
              )}
            </Box>
          </ModalHeaderContract>
          <ModalContentContainer>
            <ContractDateContainer key={`${contractToEdit.contractId} edit`}>
              <EditDayPickerRow
                weekDays={dateRows}
                onChangeHours={onChangeHours}
                currentStartWorkError={currentStartWorkError}
                currentEndWorkError={currentEndWorkError}
                formatError={hourFormatErrorIndex}
                onChangeStartBreak={onChangeStartBreak}
                isViewOnly={isViewOnly}
              />
            </ContractDateContainer>
            <ContractModalsBottomSection
              permanent={permanent}
              control={control}
              formState={formState}
              locations={locationsDropdownItems}
              functions={selectableFunctions}
              costCenters={costCenters}
              isWageVisible={isWageVisible}
              locationId={locationWatch}
              companyId={companyId}
              setValue={setValue}
              getValues={getValues}
              costCenterId={costCenterWatch}
              isEdit
              isViewOnly={isViewOnly}
            />
          </ModalContentContainer>
          <ModalFooter>
            {!isViewOnly && (
              <UPButton
                text={t(GENERAL__SAVE_BUTTON)}
                onClick={onSubmit}
                disabled={isSaveDisabled()}
                id="contract-edit-submit-button"
              />
            )}
            <UPButton outlined text={t(GENERAL__CANCEL_BUTTON)} onClick={onClose} id="contract-edit-cancel-button" />
          </ModalFooter>
        </AddContractModalContainer>
      </ModalWrapperContract>
    </Backdrop>
  );
};

export default EditContractModal;
